import React, { useEffect, useRef, useState } from "react";
import "./PDF.scss";

import { GrDocumentPdf } from "react-icons/gr";
import { IoIosArrowBack, IoMdMail } from "react-icons/io";
import { IoMdPrint } from "react-icons/io";
import { IoShareSocialSharp } from "react-icons/io5";

import { useLocation, useNavigate } from "react-router-dom";
import ROUTES from "../../constants/routes";
import { BASEURL } from "../../api/api";
import { fetchSaleInvoice, fetchSalePdf } from "../../api/sales";
import { useDispatch, useSelector } from "react-redux";
import useOrganizationIsAllowed from "../../helpers/organizationPrevillages";
import { updateOpenSubscriptionAd } from "../../redux/features/subscription-slice";
import { Share } from "@capacitor/share";
import { Capacitor } from "@capacitor/core";
import { toast } from "react-toastify";

// const PDF_LINK = process.env.REACT_APP_PDF_LINK;
const PDF_LINK = `${BASEURL}/storage/sales`;

const PDF = ({ id }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const downloadRef = useRef(null);

  const containerRef = useRef(null);
  const [pdfScale, setPdfScale] = useState(1);

  const saleId = id || location.state?.saleId;
  const userData = location.state?.userData || {};
  const salesDetails = location.state?.salesDetails || null;
  const customerData = location.state?.customerData || null;

  const { id: organization_id } = useSelector(
    (state) => state.organizationReducer.value
  );

  const orgIsAllowedToUseBill = useOrganizationIsAllowed("bill");

  const [htmlContent, setHtmlContent] = useState("");

  useEffect(() => {
    const container = containerRef.current;

    const handleResize = () => {
      if (!container) return;
      const containerWidth = container.clientWidth;
      const scaleValue = Math.min((containerWidth - 40) / 600, 1);
      setPdfScale(scaleValue);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (!saleId || !organization_id) {
      navigate(ROUTES.sales.path);
    } else {
      fetchSaleInvoice(saleId, organization_id)
        .then((res) => {
          if (res.data) {
            setHtmlContent(res.data);
          }
        })
        .catch((err) => {
          console.log("Error while fetching pdf content.");
        });
    }
  }, []);

  const printPDF = () => {
    const printWindow = window.open(`${PDF_LINK}/${saleId}.pdf`);
    printWindow.onload = function () {
      printWindow.print();
    };
  };

  const mailPDF = () => {
    const subject = encodeURIComponent("Sale Invoice PDF");
    const body = encodeURIComponent(
      `Please check the PDF at: ${PDF_LINK}/${saleId}.pdf`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const sharePDF = async () => {
    if (Capacitor.isNativePlatform()) {
      const fileUrl = `${PDF_LINK}/${saleId}.pdf`;

      try {
        await Share.share({
          title: "PDF Document",
          text: "Please find the PDF document attached.",
          url: fileUrl,
          dialogTitle: "Share PDF",
        });
      } catch (error) {
        toast.dismiss();
        toast.error("Sharing failed");
      }
    } else {
      if (navigator.share) {
        try {
          await navigator.share({
            title: "PDF Document",
            url: `${PDF_LINK}/${saleId}.pdf`,
          });
        } catch {
          toast.dismiss();
          toast.error("Sharing failed");
        }
      } else {
        alert("Sharing is not supported in your browser.");
      }
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await fetchSalePdf(saleId);
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );

      if (Capacitor.isNativePlatform()) {
        window.open(`${PDF_LINK}/${saleId}.pdf`, "_blank");
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = `${saleId}.pdf`;
        link.click();
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.dismiss();
      toast.error("Error while downloading pdf!");
    }
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };
  return (
    <div className="pdf-reader container-wrapper">
      <div className="pdf-container" ref={containerRef}>
        <div
          style={{
            transform: `scale(${pdfScale})`,
            transformOrigin: "top left",
          }}
          className="pdf-invoice"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      </div>

      <div className={`download-buttons ${id ? "desktop-side-banner" : ""}`}>
        <div
          className="item-button"
          onClick={() => {
            if (!orgIsAllowedToUseBill.exists) {
              dispatch(updateOpenSubscriptionAd({ openAd: true }));
            } else {
              handleDownloadPdf();
            }
          }}
        >
          <GrDocumentPdf />
          <p>PDF</p>
        </div>
        <div
          className="item-button"
          onClick={() => {
            if (!orgIsAllowedToUseBill.exists) {
              dispatch(updateOpenSubscriptionAd({ openAd: true }));
            } else {
              mailPDF();
            }
          }}
        >
          <IoMdMail />
          <p>Mail</p>
        </div>
        <div
          className="item-button"
          onClick={() => {
            if (!orgIsAllowedToUseBill.exists) {
              dispatch(updateOpenSubscriptionAd({ openAd: true }));
            } else {
              printPDF();
            }
          }}
        >
          <IoMdPrint />
          <p>Print</p>
        </div>
        <div
          className="item-button"
          onClick={() => {
            if (!orgIsAllowedToUseBill.exists) {
              dispatch(updateOpenSubscriptionAd({ openAd: true }));
            } else {
              sharePDF();
            }
          }}
        >
          <IoShareSocialSharp />
          <p>Share</p>
        </div>
      </div>
      {(userData?.id || customerData || salesDetails) && (
        <div
          className="navigation-back-button"
          onClick={() => {
            navigate(ROUTES.salesDetails.path, {
              state: {
                userData: userData,
                item: salesDetails,
                customerData: customerData,
              },
            });
          }}
        >
          <IoIosArrowBack />
        </div>
      )}
    </div>
  );
};

export default PDF;
